import { useEffect } from "react";

/**
 * Sitewide DefinedTermSet — anchors Rameon Energy's domain vocabulary so
 * generative engines (Google AI Overviews, Perplexity, ChatGPT browse,
 * Bing Copilot) can map our brand to canonical ZLD/MEE/MVR/ATFD concepts.
 * Mounted once on the Home page (DefinedTermSet is sitewide-scope).
 */
const TERMS: { name: string; def: string; url: string }[] = [
  {
    name: "Zero Liquid Discharge (ZLD)",
    def: "An industrial wastewater treatment strategy that eliminates liquid effluent discharge by recovering 95–99% of water for reuse and crystallising dissolved solids for safe disposal. Mandated by CPCB for several red-category Indian industries.",
    url: "https://rameonenergy.in/products/rame-zld",
  },
  {
    name: "Multi Effect Evaporator (MEE)",
    def: "A steam-driven evaporator that reuses vapour from one effect as the heating medium for the next, dramatically reducing steam consumption per kg of water evaporated. Standard core technology in Indian ZLD plants.",
    url: "https://rameonenergy.in/products/mee-evaporator",
  },
  {
    name: "Mechanical Vapour Recompression Evaporator (MVRE / MVR)",
    def: "A compressor-driven evaporator that recompresses generated vapour and reuses it as heating steam, eliminating live steam consumption. Ideal for ZLD plants targeting boiler-free operation and lowest OPEX.",
    url: "https://rameonenergy.in/products/mvre-system",
  },
  {
    name: "Agitated Thin Film Dryer (ATFD)",
    def: "A wiped-film evaporator that converts MEE/MVR concentrate into a free-flowing dry powder, completing the Zero Liquid Discharge loop and producing CPCB-compliant solid residue.",
    url: "https://rameonenergy.in/products/atfd",
  },
  {
    name: "Thermal Stripper",
    def: "An upstream column that removes ammonia, methanol or other volatile compounds from effluent before evaporation, protecting downstream MEE/MVR units in chemical and pharmaceutical ZLD plants.",
    url: "https://rameonenergy.in/products/stripper-system",
  },
  {
    name: "Falling Film Evaporator",
    def: "A vertical tube evaporator that concentrates low-viscosity effluent with very short residence time, typically used as the first effect in multi-effect ZLD trains.",
    url: "https://rameonenergy.in/products/falling-film-evaporator",
  },
  {
    name: "Boiler-Free ZLD",
    def: "A Zero Liquid Discharge configuration built around MVR and ATFD that operates entirely on electricity, eliminating coal/gas-fired boilers and associated emissions.",
    url: "https://rameonenergy.in/blog/boiler-free-zld-technology",
  },
  {
    name: "CPCB Compliance",
    def: "Adherence to discharge norms and OCEMS monitoring set by India's Central Pollution Control Board, including mandatory ZLD for textile dyeing, tannery and several chemical/pharmaceutical categories.",
    url: "https://rameonenergy.in/blog/spcb-cpcb-compliance-requirements-zld-industries",
  },
];

const GlossarySchema = () => {
  useEffect(() => {
    const existing = document.querySelector('script[data-glossary-schema="true"]');
    if (existing) existing.remove();

    const data = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      "@id": "https://rameonenergy.in/#glossary",
      name: "Rameon Energy ZLD & Evaporation Glossary",
      url: "https://rameonenergy.in/",
      hasDefinedTerm: TERMS.map((t) => ({
        "@type": "DefinedTerm",
        name: t.name,
        description: t.def,
        inDefinedTermSet: "https://rameonenergy.in/#glossary",
        url: t.url,
      })),
    };

    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-glossary-schema", "true");
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);

    return () => {
      s.remove();
    };
  }, []);

  return null;
};

export default GlossarySchema;
